import type { LoaderFunctionArgs } from "react-router";

import type { CircularFilter } from "~/features/circulars/filter";
import { filterCirculars } from "~/features/circulars/filter";
import { type Sort, sortCirculars } from "~/features/circulars/sort";
import { documentDistributionClient } from "~/features/integration/document-distribution/client.server";
import { topasUserDataClient } from "~/features/integration/topas-user-data/client.server";
import { CIRCULARS_QUERY } from "~/features/query-cache/queries";
import { fetchJson } from "~/utils/fetch-json";
import { requireUser } from "~/utils/user.server";

import type { UseQueryOptions } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import type {
  CircularAckDTO,
  CircularDTO,
} from "@vy-clients/document-distribution";
import type { Favorite } from "@vy-clients/personnel-topas-user-data";
import type { Jsonify } from "type-fest";

export type CircularsData = Jsonify<Awaited<ReturnType<typeof loader>>>;

type EnrichedCircular = CircularDTO & {
  circularAck: CircularAckDTO | undefined;
  isFavorite: boolean;
  isRecipient: boolean;
};

export type ClientEnrichedCircular = Jsonify<EnrichedCircular>;

export async function loader({ context }: LoaderFunctionArgs) {
  const { employeeId } = requireUser(context);

  const client = documentDistributionClient(context);
  const userDataClient = topasUserDataClient(context);
  const [allCirculars, employeeCirculars, circularAcks, favoriteCirculars] =
    await Promise.all([
      client.getCirculars({
        onlyCurrentlyPublished: true,
      }),
      client.getCirculars({
        onlyCurrentlyPublished: true,
        recipient: employeeId,
      }),
      client.getCircularAcks({ id: employeeId }),
      userDataClient.getFavorites({
        resourceCollection: "circulars",
      }),
    ]);

  const circulars: EnrichedCircular[] =
    allCirculars?.map((circular) => ({
      ...circular,
      circularAck: circularAcks?.find(
        (circularAck) => circularAck.circularId === circular.circularId,
      ),
      isFavorite: isFavorite(favoriteCirculars, circular.circularId),
      isRecipient: isRecipient(employeeCirculars ?? [], circular.circularId),
    })) ?? [];

  return circulars;
}

export type CircularsLoader = typeof loader;

export const circularsQueryFn = () =>
  fetchJson<CircularsData>("/api/circulars", {
    timeoutMs: 15_000,
  });

export const useCirculars = (filter: CircularFilter, sort?: Sort) => {
  return useQuery({
    ...queryFnOptions,
    queryFn: circularsQueryFn,
    select(data) {
      const filteredCirculars = filterCirculars({ circulars: data, filter });
      return sort
        ? sortCirculars({ circulars: filteredCirculars, sort })
        : filteredCirculars;
    },
  });
};

export const useOrderControlCirculars = () => {
  return useQuery({
    ...queryFnOptions,
    queryFn: circularsQueryFn,
    select(data) {
      const circulars = data.filter((it) => it.isRecipient);

      const acknowledgedCirculars = [];
      const unacknowledgedCirculars = [];

      for (const circular of circulars) {
        if (circular.circularAck?.isAcknowledged) {
          acknowledgedCirculars.push(circular);
        } else {
          unacknowledgedCirculars.push(circular);
        }
      }

      return { acknowledgedCirculars, unacknowledgedCirculars };
    },
  });
};

function isFavorite(favorites: Favorite[], circularId: string) {
  return favorites.some(
    (favorite) => favorite.resourceIdentifier === circularId,
  );
}

function isRecipient(employeeCirculars: CircularDTO[], circularId: string) {
  return employeeCirculars.some(
    (employeeCircular) => employeeCircular.circularId === circularId,
  );
}

const queryFnOptions: UseQueryOptions<CircularsData> = {
  ...CIRCULARS_QUERY,
  retry(failureCount, error) {
    if (
      error instanceof Response &&
      error.status >= 400 &&
      error.status < 500
    ) {
      // Don't retry 4xx errors
      return false;
    }

    return failureCount < 3;
  },
};
